// Set to false before going to production
const DEBUG = false;

/** @param {...unknown} args */
const log = (...args) => DEBUG && console.log('[YT Tracking]', ...args);

const PROGRESS_POLL_MS = 500;
const COMPLETE_THRESHOLD_S = 1.5;
const MILESTONES = /** @type {const} */ ([25, 50, 75]);

/**
 * @typedef {{ isVisible: () => boolean, destroy: () => void }} Visibility
 *
 * @typedef {{
 *   started: boolean,
 *   fired: Record<string, boolean>,
 *   intervalId: ReturnType<typeof setInterval> | null,
 *   percent: number
 * }} TrackState
 */

// ---------------------------------------------------------------------------
// Initialization guard
// ---------------------------------------------------------------------------

/** @type {WeakSet<HTMLIFrameElement>} */
const initializedIframes = new WeakSet();

// ---------------------------------------------------------------------------
// YouTube IFrame API loading
// ---------------------------------------------------------------------------

// If another script (e.g. GTM) already loaded the API, mark it ready immediately
let apiReady = window.YT !== undefined && window.YT.Player !== undefined;

/** @type {Array<() => void>} */
let readyQueue = [];

let apiLoading = false;

const loadYouTubeApi = () => {
  if (apiLoading || apiReady) return;
  apiLoading = true;

  // Preserve any existing callback (e.g. set up by GTM)
  const prev = window.onYouTubeIframeAPIReady;

  window.onYouTubeIframeAPIReady = () => {
    log('YouTube IFrame API ready');
    apiReady = true;
    if (typeof prev === 'function') prev();
    const queue = readyQueue;
    readyQueue = [];
    for (const cb of queue) cb();
  };

  if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    document.head.appendChild(tag);
    log('YouTube IFrame API script injected');
  }
};

/** @param {() => void} cb */
const whenApiReady = (cb) => {
  if (apiReady) {
    cb();
    return;
  }

  readyQueue.push(cb);
  loadYouTubeApi();
};

// ---------------------------------------------------------------------------
// Iframe helpers
// ---------------------------------------------------------------------------

/**
 * @param {Element} el
 * @returns {el is HTMLIFrameElement}
 */
const isYouTubeIframe = (el) => el.tagName === 'IFRAME' && (el.getAttribute('src') ?? '').includes('youtube.com/embed');

/** @param {HTMLIFrameElement} iframe */
const ensureJsApi = (iframe) => {
  const src = iframe.getAttribute('src') ?? '';
  if (!src || src.includes('enablejsapi=1')) return;

  try {
    const url = new URL(src);
    url.searchParams.set('enablejsapi', '1');
    iframe.src = url.toString();
    log('Added enablejsapi=1 to', src);
  } catch {
    // Fallback: string concatenation if URL constructor isn't available
    iframe.src = src + (src.includes('?') ? '&' : '?') + 'enablejsapi=1';
    log('Added enablejsapi=1 (fallback) to', src);
  }
};

// ---------------------------------------------------------------------------
// Visibility tracking
// ---------------------------------------------------------------------------

/** @param {HTMLIFrameElement} iframe */
const createVisibility = (iframe) => {
  let visible = false;
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) visible = entry.isIntersecting;
    },
    { threshold: 0.5 }
  );
  observer.observe(iframe);

  return /** @type {Visibility} */ ({
    isVisible: () => visible,
    destroy: () => observer.disconnect(),
  });
};

// ---------------------------------------------------------------------------
// dataLayer push
// ---------------------------------------------------------------------------

/**
 * @param {string} eventName
 * @param {YT.Player} player
 * @param {TrackState} state
 * @param {Visibility} visibility
 */
const pushEvent = (eventName, player, state, visibility) => {
  const iframe = player.getIframe();

  const payload = {
    event: eventName,
    video_provider: 'youtube',
    video_title: iframe.getAttribute('title') ?? iframe.getAttribute('aria-label') ?? '',
    video_url: iframe.src ?? '',
    video_duration: Math.round(player.getDuration() ?? 0),
    video_current_time: Math.round(player.getCurrentTime() ?? 0),
    ...(eventName !== 'video_start' && { video_completion: state.percent }),
    video_visible: visibility.isVisible(),
    timestamp: new Date().toISOString(),
  };

  log('Pushing', eventName, payload);
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
};

// ---------------------------------------------------------------------------
// Progress polling
// ---------------------------------------------------------------------------

/**
 * @param {YT.Player} player
 * @param {TrackState} state
 * @param {Visibility} visibility
 */
const startPolling = (player, state, visibility) => {
  if (state.intervalId !== null) return;

  state.intervalId = setInterval(() => {
    const duration = player.getDuration();
    if (!duration || duration <= 0) return;

    const current = player.getCurrentTime();
    const pct = (current / duration) * 100;

    for (const m of MILESTONES) {
      if (!state.fired[m] && pct >= m) {
        state.fired[m] = true;
        state.percent = m;
        pushEvent('video_progress', player, state, visibility);
      }
    }

    if (!state.fired.complete && duration - current <= COMPLETE_THRESHOLD_S) {
      state.fired.complete = true;
      state.percent = 100;
      pushEvent('video_complete', player, state, visibility);
      stopPolling(state);
    }
  }, PROGRESS_POLL_MS);
};

/** @param {TrackState} state */
const stopPolling = (state) => {
  if (state.intervalId === null) return;
  clearInterval(state.intervalId);
  state.intervalId = null;
};

// ---------------------------------------------------------------------------
// Player initialisation
// ---------------------------------------------------------------------------

let playerIdSeq = 0;

/** @param {HTMLIFrameElement} iframe */
const setupPlayer = (iframe) => {
  if (!iframe.id) iframe.id = `yt-track-${++playerIdSeq}`;

  /** @type {TrackState} */
  const state = { started: false, fired: {}, intervalId: null, percent: 0 };

  // Stub replaced in onReady once we know the exact iframe the YT API bound to
  // (the API may replace the element if enablejsapi was absent from the original src)
  /** @type {Visibility} */
  let visibility = { isVisible: () => false, destroy: () => {} };

  log('Initialising player:', iframe.id, iframe.src);

  const ytPlayer = new YT.Player(iframe.id, {
    events: {
      onReady: () => {
        visibility.destroy();
        visibility = createVisibility(ytPlayer.getIframe());
        log('Player ready:', iframe.id);
      },

      onStateChange: (e) => {
        const playerState = e.data;
        const player = ytPlayer;
        log('State change:', playerState, 'for', iframe.id);

        if (playerState === YT.PlayerState.PLAYING) {
          if (!state.started) {
            state.started = true;
            state.percent = 0;
            pushEvent('video_start', player, state, visibility);
          } else if (state.fired.complete) {
            // Video restarted after completion (manual replay or loop)
            log('Video restarted, resetting progress for', iframe.id);
            state.fired = {};
            state.percent = 0;
            pushEvent('video_start', player, state, visibility);
          }
          startPolling(player, state, visibility);
          return;
        }

        if (playerState === YT.PlayerState.PAUSED) {
          stopPolling(state);
          return;
        }

        if (playerState === YT.PlayerState.ENDED) {
          stopPolling(state);
          if (!state.fired.complete) {
            state.fired.complete = true;
            state.percent = 100;
            pushEvent('video_complete', player, state, visibility);
          }
        }
      },
    },
  });
};

// ---------------------------------------------------------------------------
// Iframe discovery
// ---------------------------------------------------------------------------

/** @param {Element} el */
const processIframe = (el) => {
  if (!isYouTubeIframe(el)) return;

  const iframe = /** @type {HTMLIFrameElement} */ (el);
  if (initializedIframes.has(iframe)) return;

  // Mark early so rapid duplicate calls (observer + scanPage) don't double-queue
  initializedIframes.add(iframe);
  ensureJsApi(iframe);

  whenApiReady(() => {
    if (!iframe.isConnected) return;
    setupPlayer(iframe);
  });
};

const scanPage = () => {
  log('Scanning page for YouTube iframes');
  for (const iframe of document.querySelectorAll('iframe')) processIframe(iframe);
};

// ---------------------------------------------------------------------------
// MutationObserver — catch iframes added dynamically (deferred-media play)
// ---------------------------------------------------------------------------

const domObserver = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (node.nodeType !== Node.ELEMENT_NODE) continue;

      const el = /** @type {Element} */ (node);

      if (el.tagName === 'IFRAME') {
        processIframe(el);
      } else {
        for (const iframe of el.querySelectorAll('iframe')) processIframe(iframe);
      }
    }
  }
});

domObserver.observe(document.body, { childList: true, subtree: true });

// ---------------------------------------------------------------------------
// Bootstrap
// ---------------------------------------------------------------------------

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', scanPage);
} else {
  scanPage();
}

// Re-scan when the Shopify theme editor reloads a section
document.addEventListener('shopify:section:load', ({ target }) => {
  if (!(target instanceof HTMLElement)) return;
  for (const iframe of target.querySelectorAll('iframe')) processIframe(iframe);
});
